<div class="text-fg w-full">
  <!-- Loading -->
  @if (loading()) {
    <div class="flex items-center justify-center gap-2 py-8 text-muted">
      <ui-spinner size="sm" />
      <span class="text-xs">Loading journal entry...</span>
    </div>
  }

  <!-- Error -->
  @if (error(); as err) {
    <ui-alert variant="danger">{{ err }}</ui-alert>
  }

  <!-- Success (after reversal) -->
  @if (reverseSuccess(); as msg) {
    <ui-alert variant="success">
      <div class="flex items-center justify-between gap-3">
        <span>{{ msg }}</span>
        <button type="button" class="cursor-pointer border-0 bg-transparent text-lg leading-none text-current" (click)="reverseSuccess.set(null)">&times;</button>
      </div>
    </ui-alert>
  }

  @if (entry(); as e) {
    <!-- Header -->
    <header class="mb-4 flex items-start justify-between gap-4">
      <div>
        <button type="button" class="mb-2 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-muted hover:text-fg" (click)="goBack()">&larr; Back</button>
        <h1 class="m-0 flex flex-wrap items-center gap-3 font-heading text-2xl font-bold text-fg">
          <ui-badge [variant]="getEventTypeVariant(e.event_type)">{{ e.event_type_display }}</ui-badge>
          <span class="cursor-pointer font-mono text-xl" (click)="copyToClipboard(e.transaction_reference, $event)" title="Click to copy">
            {{ e.transaction_reference }}
          </span>
        </h1>
        <p class="mb-0 mt-1 text-sm text-muted">{{ formatDate(e.created_at) }}</p>
      </div>
      <div class="flex items-center gap-2">
        @if (!e.is_reversed && e.event_type !== 'REVERSAL') {
          <button type="button" uiButton variant="outline" (click)="openReverseModal()">
            <ng-icon name="lucideUndo2" size="16" />
            <span>Reverse</span>
          </button>
        }
        <button type="button" uiButton variant="outline" aria-label="Refresh" (click)="load()">
          <ng-icon name="lucideRefreshCw" size="16" />
        </button>
      </div>
    </header>

    <!-- Status Indicators -->
    <div class="mb-4 flex flex-wrap items-center gap-2">
      @if (e.is_balanced) {
        <ui-badge variant="success">
          <ng-icon name="lucideCheck" size="14" />
          <span>Balanced</span>
        </ui-badge>
      } @else {
        <ui-badge variant="danger">
          <ng-icon name="lucideX" size="14" />
          <span>Unbalanced</span>
        </ui-badge>
      }
      @if (e.is_reversed) {
        <ui-badge variant="warning">
          <ng-icon name="lucideTriangleAlert" size="14" />
          <span>Reversed</span>
        </ui-badge>
      }
      @if (e.event_type === 'REVERSAL' && e.reversed_entry) {
        <ui-badge variant="info">
          <ng-icon name="lucideLink" size="14" />
          <span>Reversal of
            <a [routerLink]="shellRoutes.commands('manage', entityKey, journalLinkId(e.reversed_entry.id))" class="underline">
              {{ e.reversed_entry.transaction_reference }}
            </a>
          </span>
        </ui-badge>
      }
      @if (e.is_reversed && e.reversal_entry) {
        <ui-badge variant="warning">
          <ng-icon name="lucideLink" size="14" />
          <span>Reversed by
            <a [routerLink]="shellRoutes.commands('manage', entityKey, journalLinkId(e.reversal_entry.id))" class="underline">
              {{ e.reversal_entry.transaction_reference }}
            </a>
          </span>
        </ui-badge>
      }
      <ui-badge>
        {{ e.ledger_lines_count }} line{{ e.ledger_lines_count !== 1 ? 's' : '' }}
      </ui-badge>
    </div>

    <!-- General Info -->
    <section class="mb-4 rounded-lg border border-border bg-card p-5">
      <h3 class="mb-3 mt-0 text-xs font-bold uppercase tracking-wide text-muted">Details</h3>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Description</span>
            <span class="text-sm text-fg">{{ e.description }}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Event Type</span>
            <span class="text-sm text-fg">{{ e.event_type_display }}</span>
          </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Reference</span>
          <span class="inline-flex cursor-pointer items-center gap-1 font-mono text-sm text-fg" (click)="copyToClipboard(e.transaction_reference, $event)">
            {{ e.transaction_reference }}
            <ng-icon name="lucideClipboard" size="12" />
          </span>
        </div>
        @if (e.transaction_type) {
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Transaction Type</span>
            <span class="text-sm text-fg">{{ e.transaction_type }}</span>
          </div>
        }
        @if (e.transaction_object) {
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Linked Transaction</span>
            <span class="font-mono text-sm text-fg">{{ e.transaction_object.type }} / {{ e.transaction_object.id }}</span>
          </div>
        }
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Created</span>
            <span class="text-sm text-fg">{{ formatDate(e.created_at) }}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">Updated</span>
            <span class="text-sm text-fg">{{ formatDate(e.updated_at) }}</span>
          </div>
      </div>
    </section>

    <!-- Metadata -->
    @if (metadataKeys().length > 0) {
      <section class="mb-4 rounded-lg border border-border bg-card p-5">
        <h3 class="mb-3 mt-0 text-xs font-bold uppercase tracking-wide text-muted">Metadata</h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
          @for (key of metadataKeys(); track key) {
            <div class="flex flex-col gap-0.5 rounded-md bg-surface-muted px-3 py-2">
              <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">{{ key }}</span>
              <span class="break-all font-mono text-xs text-fg">{{ metadataValue(key) }}</span>
            </div>
          }
        </div>
      </section>
    }

    <!-- Ledger Lines: Debits -->
    <section>
      <h3 class="mb-3 mt-6 text-xs font-bold uppercase tracking-wide text-muted">Debit Lines</h3>
      @if (getDebitLines().length === 0) {
        <p class="m-0 text-sm text-muted">No debit lines.</p>
      } @else {
        <div uiTableWrap>
          <table uiTable>
            <thead>
              <tr>
                <th>Account</th>
                <th>Name</th>
                <th>Type</th>
                <th class="text-right">Amount</th>
                <th>Currency</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              @for (line of getDebitLines(); track line.id) {
                <tr>
                  <td class="font-mono">{{ line.account_code }}</td>
                  <td>{{ line.account_name }}</td>
                  <td><ui-badge>{{ line.account_type }}</ui-badge></td>
                  <td class="text-right font-mono text-danger-fg">{{ formatAmount(line.amount) }}</td>
                  <td class="font-mono">{{ line.currency_code }}</td>
                  <td class="max-w-64 text-xs text-fg-secondary">{{ line.description }}</td>
                </tr>
              }
            </tbody>
            <tfoot>
              @for (currency of getDebitCurrencies(); track currency) {
                <tr class="bg-surface-muted font-bold">
                  <td colspan="3" class="text-right text-xs uppercase text-muted">Total ({{ currency }})</td>
                  <td class="text-right font-mono text-danger-fg">{{ getDebitTotal(currency) }}</td>
                  <td class="font-mono">{{ currency }}</td>
                  <td></td>
                </tr>
              }
            </tfoot>
          </table>
        </div>
      }
    </section>

    <!-- Ledger Lines: Credits -->
    <section>
      <h3 class="mb-3 mt-6 text-xs font-bold uppercase tracking-wide text-muted">Credit Lines</h3>
      @if (getCreditLines().length === 0) {
        <p class="m-0 text-sm text-muted">No credit lines.</p>
      } @else {
        <div uiTableWrap>
          <table uiTable>
            <thead>
              <tr>
                <th>Account</th>
                <th>Name</th>
                <th>Type</th>
                <th class="text-right">Amount</th>
                <th>Currency</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              @for (line of getCreditLines(); track line.id) {
                <tr>
                  <td class="font-mono">{{ line.account_code }}</td>
                  <td>{{ line.account_name }}</td>
                  <td><ui-badge>{{ line.account_type }}</ui-badge></td>
                  <td class="text-right font-mono text-success-fg">{{ formatAmount(line.amount) }}</td>
                  <td class="font-mono">{{ line.currency_code }}</td>
                  <td class="max-w-64 text-xs text-fg-secondary">{{ line.description }}</td>
                </tr>
              }
            </tbody>
            <tfoot>
              @for (currency of getCreditCurrencies(); track currency) {
                <tr class="bg-surface-muted font-bold">
                  <td colspan="3" class="text-right text-xs uppercase text-muted">Total ({{ currency }})</td>
                  <td class="text-right font-mono text-success-fg">{{ getCreditTotal(currency) }}</td>
                  <td class="font-mono">{{ currency }}</td>
                  <td></td>
                </tr>
              }
            </tfoot>
          </table>
        </div>
      }
    </section>
  }

  <!-- Reverse Confirmation Modal -->
  <ui-modal title="Confirm Reversal" [open]="reverseModalOpen()" (closed)="closeReverseModal()">
    @if (entry(); as e) {
      <p class="mb-3 mt-0 text-sm text-fg-secondary">
        You are about to reverse journal entry
        <strong class="font-mono text-fg">{{ e.transaction_reference }}</strong>.
      </p>
      <p class="mb-3 mt-0 text-sm text-fg-secondary">
        This will create a new <strong>REVERSAL</strong> entry with all debit and credit lines swapped,
        effectively undoing this operation. Wallet balances will be updated accordingly.
      </p>
      <p class="m-0 text-sm font-semibold text-danger-fg">This action cannot be undone.</p>
    }
    @if (reverseError(); as err) {
      <ui-alert class="mt-3" variant="danger">{{ err }}</ui-alert>
    }
    <ng-container ngProjectAs="[modal-footer]">
      <button type="button" uiButton variant="outline" (click)="closeReverseModal()">Cancel</button>
      <button type="button" uiButton variant="danger" [disabled]="reversing()" (click)="confirmReverse()">
        @if (reversing()) {
          <ui-spinner size="sm" />
        } @else {
          <ng-icon name="lucideUndo2" size="14" />
        }
        <span>Reverse Entry</span>
      </button>
    </ng-container>
  </ui-modal>

  <!-- Copy toast -->
  @if (copyToast()) {
    <div class="fixed bottom-6 left-1/2 z-1100 -translate-x-1/2 rounded-md bg-fg px-4 py-2 text-sm font-semibold text-card shadow-lg">Copied!</div>
  }
</div>
